/**
 * MarqueeOverlay — partial-region inpaint UX (Jun 24).
 *
 * Renders on top of a previewRef element (typically the rendered creative
 * canvas). When `enabled`, the overlay captures all mouse events on the
 * preview, lets the user drag a rectangle, and exposes an instruction
 * textarea + Apply / Cancel buttons next to the rect.
 *
 * Coordinates are normalized to the preview's pixel size and exposed as
 * fractional 0-1 values via `onApply(rect, instruction)` — the backend
 * scales them to the base image's native dimensions and builds a mask.
 *
 * The component is INTENTIONALLY dumb — it owns no network state. The
 * parent (StudioFinishEditor) controls `applying` (spinner) and clears
 * the overlay by flipping `enabled=false` after a successful apply.
 */

import { useEffect, useRef, useState } from 'react';

type RectFrac = { x: number; y: number; w: number; h: number };

const MIN_RECT_PX = 24; // smaller than this and the user almost certainly mis-clicked

interface MarqueeOverlayProps {
  enabled: boolean;
  applying: boolean;
  /** Pixel ref to the preview surface the rect is drawn over. */
  previewRef: React.RefObject<HTMLElement | null>;
  onApply: (rect: RectFrac, instruction: string) => void;
  onCancel: () => void;
  /** Optional — only shown when undoStackSize > 0. */
  undoStackSize?: number;
  onUndo?: () => void;
  undoing?: boolean;
}

export default function MarqueeOverlay({
  enabled,
  applying,
  previewRef,
  onApply,
  onCancel,
  undoStackSize = 0,
  onUndo,
  undoing = false,
}: MarqueeOverlayProps) {
  // Local rect state — pixel coords RELATIVE to the preview's
  // bounding rect (so the overlay positions itself correctly).
  const [rectPx, setRectPx] = useState<{
    x: number; y: number; w: number; h: number;
  } | null>(null);
  // isDragging mirrors draggingRef in React state. Drives the "show
  // dim + pulse + panel only AFTER mouseup" UX — during drag we render
  // ONLY the rect outline so the canvas stays calm + flicker-free.
  const [isDragging, setIsDragging] = useState(false);
  const [instruction, setInstruction] = useState('');
  const draggingRef = useRef(false);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);

  // Reset state when the tool toggles off.
  useEffect(() => {
    if (!enabled) {
      setRectPx(null);
      setIsDragging(false);
      setInstruction('');
      draggingRef.current = false;
      dragStartRef.current = null;
    }
  }, [enabled]);

  // Mouse handlers — bound on the OVERLAY div (which sits inside the
  // preview and covers it 100%/100%). Coords are relative to preview.
  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!enabled || applying) return;
    if (e.button !== 0) return;
    const previewRect = previewRef.current?.getBoundingClientRect();
    if (!previewRect) return;
    const x = e.clientX - previewRect.left;
    const y = e.clientY - previewRect.top;
    draggingRef.current = true;
    dragStartRef.current = { x, y };
    setIsDragging(true);
    setRectPx({ x, y, w: 0, h: 0 });
    setInstruction(''); // fresh selection clears any prior text
    e.preventDefault();
  };

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!draggingRef.current || !dragStartRef.current) return;
    const previewRect = previewRef.current?.getBoundingClientRect();
    if (!previewRect) return;
    const cx = Math.max(0, Math.min(previewRect.width, e.clientX - previewRect.left));
    const cy = Math.max(0, Math.min(previewRect.height, e.clientY - previewRect.top));
    const sx = dragStartRef.current.x;
    const sy = dragStartRef.current.y;
    setRectPx({
      x: Math.min(sx, cx),
      y: Math.min(sy, cy),
      w: Math.abs(cx - sx),
      h: Math.abs(cy - sy),
    });
  };

  const onMouseUp = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    dragStartRef.current = null;
    setIsDragging(false);
    setRectPx((r) => {
      if (!r) return null;
      // Discard rects too small to be a meaningful selection.
      if (r.w < MIN_RECT_PX || r.h < MIN_RECT_PX) return null;
      return r;
    });
  };

  // Window-level mouseup catches drags that release outside the overlay.
  useEffect(() => {
    if (!enabled) return;
    const handle = () => onMouseUp();
    window.addEventListener('mouseup', handle);
    return () => window.removeEventListener('mouseup', handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const previewSize = previewRef.current?.getBoundingClientRect();

  const handleApply = () => {
    if (!rectPx || !instruction.trim() || applying) return;
    if (!previewSize || previewSize.width === 0 || previewSize.height === 0) return;
    onApply(
      {
        x: rectPx.x / previewSize.width,
        y: rectPx.y / previewSize.height,
        w: rectPx.w / previewSize.width,
        h: rectPx.h / previewSize.height,
      },
      instruction.trim(),
    );
  };

  const handleClearRect = () => {
    setRectPx(null);
    setInstruction('');
  };

  if (!enabled) return null;

  // Position the instruction panel:
  //   default: to the RIGHT of the rect, ~12px gap
  //   if it would overflow preview width, flip to the LEFT
  //   vertically: aligned to the rect top, clamped to stay visible
  let panelLeft = 0, panelTop = 0;
  let panelSide: 'right' | 'left' | 'top' = 'right';
  if (rectPx && previewSize) {
    const PANEL_W = 280;
    const PANEL_H = 200; // rough estimate for clamping
    const GAP = 12;
    const rightEdgeIfRight = rectPx.x + rectPx.w + GAP + PANEL_W;
    if (rightEdgeIfRight <= previewSize.width) {
      panelLeft = rectPx.x + rectPx.w + GAP;
      panelTop = Math.max(0, Math.min(previewSize.height - PANEL_H, rectPx.y));
      panelSide = 'right';
    } else if (rectPx.x - GAP - PANEL_W >= 0) {
      panelLeft = rectPx.x - GAP - PANEL_W;
      panelTop = Math.max(0, Math.min(previewSize.height - PANEL_H, rectPx.y));
      panelSide = 'left';
    } else {
      // Stack BELOW the rect; fall back to ABOVE if no room.
      const belowTop = rectPx.y + rectPx.h + GAP;
      if (belowTop + PANEL_H <= previewSize.height) {
        panelLeft = Math.max(0, Math.min(previewSize.width - PANEL_W, rectPx.x));
        panelTop = belowTop;
        panelSide = 'top';
      } else {
        panelLeft = Math.max(0, Math.min(previewSize.width - PANEL_W, rectPx.x));
        panelTop = Math.max(0, rectPx.y - GAP - PANEL_H);
        panelSide = 'top';
      }
    }
  }

  return (
    <>
      {/* Capture surface — covers the entire preview so mousedown lands
          here, not on the underlying <img>. Cursor flips to crosshair to
          telegraph the marquee mode. The semi-transparent overlay only
          renders once a rect exists (so the canvas stays visible while the
          user picks where to drag). */}
      <div
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        className="absolute inset-0 z-40"
        style={{ cursor: applying ? 'wait' : 'crosshair' }}
      >
        {/* DURING DRAG — render ONLY the rect outline, no dim, no pulse,
            no panel. Keeps the canvas calm + the cursor follows smoothly. */}
        {rectPx && isDragging && (
          <div
            className="absolute border-2 border-primary pointer-events-none"
            style={{
              left: `${rectPx.x}px`,
              top: `${rectPx.y}px`,
              width: `${rectPx.w}px`,
              height: `${rectPx.h}px`,
            }}
          />
        )}
        {/* AFTER MOUSEUP — settled state: dim surround + pulse + panel. */}
        {rectPx && !isDragging && (
          <>
            <div className="absolute inset-x-0 top-0 bg-black/40 pointer-events-none"
                 style={{ height: `${rectPx.y}px` }} />
            <div className="absolute inset-x-0 bottom-0 bg-black/40 pointer-events-none"
                 style={{ top: `${rectPx.y + rectPx.h}px` }} />
            <div className="absolute bg-black/40 pointer-events-none"
                 style={{ left: 0, top: `${rectPx.y}px`, width: `${rectPx.x}px`, height: `${rectPx.h}px` }} />
            <div className="absolute bg-black/40 pointer-events-none"
                 style={{ left: `${rectPx.x + rectPx.w}px`, top: `${rectPx.y}px`, right: 0, height: `${rectPx.h}px` }} />
            <div
              className="absolute border-2 border-primary pointer-events-none animate-pulse"
              style={{
                left: `${rectPx.x}px`,
                top: `${rectPx.y}px`,
                width: `${rectPx.w}px`,
                height: `${rectPx.h}px`,
              }}
            />
          </>
        )}
      </div>

      {/* Floating toolbar — top-right of preview. Undo only (exit lives in the
          help bar's "Exit Marquee" button now). */}
      {undoStackSize > 0 && onUndo && (
        <div className="absolute top-3 right-3 z-50 flex items-center gap-2 pointer-events-auto">
          <button
            type="button"
            onClick={onUndo}
            disabled={undoing || applying}
            className="h-9 px-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur-sm shadow-md text-xs font-bold text-dark cursor-pointer hover:text-primary disabled:opacity-50 disabled:cursor-wait"
            title={`Undo last marquee edit (${undoStackSize} on stack)`}
          >
            {undoing ? (
              <i className="ri-loader-4-line animate-spin" />
            ) : (
              <i className="ri-arrow-go-back-line" />
            )}
            Undo ({undoStackSize})
          </button>
        </div>
      )}

      {/* Instruction panel — only shows once a rect is drawn AND the drag
          has settled (mouseup). Hidden while `applying` so it doesn't cover the
          render loading overlay. Hiding during drag is the flicker fix. */}
      {rectPx && !isDragging && !applying && (
        <div
          className="absolute z-50 w-[280px] rounded-xl bg-white shadow-[0_25px_50px_-12px_rgba(15,15,30,0.45)] ring-1 ring-surface-dark/40 overflow-hidden pointer-events-auto"
          style={{ left: `${panelLeft}px`, top: `${panelTop}px` }}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseMove={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
        >
          <div className="px-3 pt-3 pb-2 flex items-center justify-between border-b border-surface-dark/30">
            <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-dark-muted">
              <i className="ri-edit-box-line mr-1 text-primary" /> Edit selection
            </span>
            <button
              type="button"
              onClick={handleClearRect}
              disabled={applying}
              className="text-xs text-dark-muted hover:text-dark cursor-pointer disabled:opacity-50"
              title="Clear selection"
            >
              Redraw
            </button>
          </div>
          <textarea
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            placeholder="e.g. Remove the text&#10;Change to read 'New Year Sale'&#10;Replace with a red bottle"
            rows={3}
            maxLength={500}
            disabled={applying}
            autoFocus
            className="w-full px-3 py-2 text-sm text-dark resize-none focus:outline-none focus:bg-surface/50 placeholder:text-dark-muted/60 disabled:opacity-50"
          />
          <div className="px-3 pb-3 flex items-center justify-end gap-2 bg-surface/30">
            <button
              type="button"
              onClick={handleClearRect}
              disabled={applying}
              className="h-8 px-3 text-xs font-bold text-dark-muted hover:text-dark cursor-pointer disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={applying || !instruction.trim()}
              className="h-8 px-4 inline-flex items-center gap-1.5 rounded-full bg-primary text-white text-xs font-bold cursor-pointer hover:bg-primary-hover disabled:opacity-50 disabled:cursor-wait"
            >
              {applying ? (
                <>
                  <i className="ri-loader-4-line animate-spin" /> Editing…
                </>
              ) : (
                <>
                  <i className="ri-magic-line" /> Apply edit · 1 cr
                </>
              )}
            </button>
          </div>
          {/* Tiny arrow visual cue pointing back at the rect */}
          {panelSide === 'right' && (
            <div className="absolute top-3 -left-1 w-2 h-2 rotate-45 bg-white ring-1 ring-surface-dark/40" />
          )}
          {panelSide === 'left' && (
            <div className="absolute top-3 -right-1 w-2 h-2 rotate-45 bg-white ring-1 ring-surface-dark/40" />
          )}
        </div>
      )}
    </>
  );
}
